import React, { cloneElement, Children, useRef, useEffect, useState } from 'react';
import { StepperFooter } from './stepper-footer';
import { StepperHeader } from './stepper-header';

export interface StepConfig {
    id: string;
    label: string;
    isInvalid: (data: unknown) => boolean;
    isComplete: (data: unknown) => boolean;
}

export interface StepProps {
    id: string;
    label: string;
    isInvalid: boolean;
    isComplete: boolean;
    children: React.ReactNode;
}

export function Step({ children }: StepProps) {
    return <div>{children}</div>;
}

Step.displayName = 'Step';

export interface StepperProps {
    currentStepIndex?: number;
    onBeforeStepChange?: (fromStepIndex: number, toStepIndex: number) => void;
    onStepChange?: (stepIndex: number) => void;
    onSubmit: () => void;
    children: React.ReactElement<StepProps> | React.ReactElement<StepProps>[];
    disableSubmit?: boolean;
    submitTooltipText?: string;
}

export interface StepContentProps {
    data: unknown;
    onChange: (data: unknown) => void;
    nextStep?: () => void;
    onNextStep?: (handler: () => void) => void;
    setIsNextStepLoading?: (isLoading: boolean) => void;
}

export function Stepper({
    currentStepIndex,
    onBeforeStepChange,
    onStepChange,
    onSubmit,
    children,
    disableSubmit,
    submitTooltipText,
}: StepperProps) {
    const [activeStep, setActiveStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const nextStepRef = useRef<() => void>();

    const steps = Children.toArray(children).filter(
        (child): child is React.ReactElement<StepProps> => React.isValidElement(child) && child.type === Step
    );

    if (steps.length === 0) {
        return <p className="text-center">No steps were found.</p>;
    }

    const current = currentStepIndex ?? activeStep;
    const isLastStep = current === steps.length - 1;

    const goToStep = (index: number) => {
        const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
        if (nextIndex !== current) {
            onBeforeStepChange?.(current, nextIndex);
        }
        onStepChange?.(nextIndex) ?? setActiveStep(nextIndex);
    };

    const nextStep = () => goToStep(current + 1);
    const prevStep = () => goToStep(current - 1);

    const handleNext = () => {
        if (isLastStep) {
            onSubmit();
        } else {
            nextStepRef.current?.() ?? nextStep();
        }
    };

    const registerNextHandler = (handler: () => void) => {
        nextStepRef.current = handler;
    };

    useEffect(() => {
        nextStepRef.current = nextStep;
    }, [current]);

    const stepConfigs = steps.map((step) => ({
        id: step.props.id,
        label: step.props.label,
        isInvalid: () => step.props.isInvalid,
        isComplete: () => step.props.isComplete,
    }));

    const stepStatuses = steps.reduce(
        (acc, step) => {
            acc[step.props.id] = {
                isInvalid: step.props.isInvalid,
                isComplete: step.props.isComplete,
            };
            return acc;
        },
        {} as Record<string, { isInvalid: boolean; isComplete: boolean }>
    );

    return (
        <>
            <StepperHeader
                currentStepIndex={current}
                stepStatuses={stepStatuses}
                goToStep={goToStep}
                steps={stepConfigs}
            />

            <div className="d-flex flex-column flex-fill">
                {steps.map((step, index) => {
                    if (index !== current) return null;

                    const enhancedChildren = React.isValidElement(step.props.children)
                        ? cloneElement(step.props.children, {
                              nextStep,
                              onNextStep: registerNextHandler,
                              setIsNextStepLoading: setIsLoading,
                              goBack: current > 0 ? prevStep : undefined,
                          } as Record<string, unknown>)
                        : step.props.children;
                    return (
                        <div key={step.props.id} className="d-flex flex-column flex-fill">
                            {enhancedChildren}
                        </div>
                    );
                })}
            </div>

            <StepperFooter
                currentStepIndex={current}
                isNextStepLoading={isLoading}
                onBack={prevStep}
                onNext={handleNext}
                onSubmit={onSubmit}
                stepCount={steps.length}
                disableSubmit={disableSubmit}
                submitTooltipText={submitTooltipText}
            />
        </>
    );
}
